import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { zIndexes } from "@shared/common/Themes/AppTheme.ts";
import CoinAccountsPieChart from "features/portfolio/Accounts/CoinAccountsPieChart.tsx";
import { SmartTableLayout } from "common/Layouts/SmartTableLayout.tsx";
import CoinAccountsTable from "features/portfolio/CoinDrawer/CoinAccountsTable.tsx";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  GetCoinExtraExchangesDataRequest,
  ProcessedCoinExchangesData,
} from "@shared/types";
import CoinAccountsCards from "features/portfolio/CoinDrawer/CoinAccountsCards.tsx";
import useMemoizedSelector from "@shared/utils/hooks/useMemoizedSelector.ts";
import {
  selectCoinAccountsData,
  selectCoinAccountsPrecision,
} from "@shared/slices/portfolio.slice.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { ExtraDetailsDrawerContext } from "common/ExtraDetails/ExtraDetailsDrawer.context.ts";
import { COIN_EXTRA_DETAILS_TABS } from "common/constants.ts";
import { SkipToken } from "@reduxjs/toolkit/query";

interface AccountsTabProps {
  args: GetCoinExtraExchangesDataRequest | SkipToken;
  isDrawerOpen?: boolean;
}

const DrawerAccountsTab = ({ args, isDrawerOpen }: AccountsTabProps) => {
  const { setTabQueries } = useContext(ExtraDetailsDrawerContext) || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const precisionSelector = useMemo(
    () => selectCoinAccountsPrecision(args),
    [args]
  );
  const data: ProcessedCoinExchangesData[] = useMemoizedSelector({
    selectorCreator: selectCoinAccountsData,
    args,
  });
  const getCoinExtraExchangesDataQuery =
    sharedLib.sharedApi.portfolio.useGetCoinExtraExchangesDataQuery(args, {
      skip: !isDrawerOpen,
    });

  useEffect(() => {
    setTabQueries?.(COIN_EXTRA_DETAILS_TABS.ACCOUNTS, [
      getCoinExtraExchangesDataQuery,
    ]);
  }, [getCoinExtraExchangesDataQuery]);

  const dataWithKey = useMemo(
    () => data.map((coinData) => ({ ...coinData, key: coinData.client })) || [],
    [data]
  );

  const renderTopContainer = useCallback(
    () => (
      <div style={TOP_CONTAINER_STYLE}>
        <CoinAccountsPieChart args={args} />
      </div>
    ),
    [args]
  );

  const tableComponent = useMemo(() => {
    return (
      <SmartTableLayout
        withSearch={true}
        isDrawerTable={true}
        tableComponent={
          <CoinAccountsTable
            data={dataWithKey}
            precisionSelector={precisionSelector}
          />
        }
      />
    );
  }, [dataWithKey, precisionSelector]);

  const table = (
    <>
      {renderTopContainer()}
      {tableComponent}
    </>
  );

  const cards = useMemo(
    () => (
      <CoinAccountsCards
        data={dataWithKey}
        renderTopContainer={renderTopContainer}
        precisionSelector={precisionSelector}
      />
    ),
    [dataWithKey, renderTopContainer, precisionSelector]
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={!getCoinExtraExchangesDataQuery.currentData}
      zIndex={zIndexes.drawer}
      hideContentWhileLoading={true}
      spinnerSize="large"
    >
      {isBottomNavigator ? cards : table}
    </LoadingSpinnerOverlay>
  );
};

export default DrawerAccountsTab;

const TOP_CONTAINER_STYLE = { marginBottom: 8 };
